<template>
  <v-dialog
    v-model="dialogProxy"
    :max-width="showPreview ? 'calc(100% - 80px)' : '1280px'"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="headline d-flex align-center">
        <span>Certificate of Quality</span>
        <v-spacer />

        <v-btn
          v-if="showPreview"
          icon
          class="mr-2"
          @click="showPreview = false"
        >
          <v-icon>mdi-eye-off</v-icon>
        </v-btn>

        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <div class="quality-dialog-layout">
          <div :class="['quality-form-pane', showPreview ? 'with-preview' : '']">
            <div class="pa-6">
              <v-btn
                  color="primary"
                  outlined
                  class="mb-4"
                  @click="addItem"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Add item
                </v-btn>
              <v-alert
                outlined
                dense
                type="info"
                class="mb-4"
              >
                The document will generate a separate page for each product in a single PDF file.
              </v-alert>

              <v-card
                v-for="(item, index) in form.items"
                :key="'quality-item-' + (item.sales_order_item_id || index)"
                outlined
                class="mb-4"
              >
                <v-card-title class="subtitle-1 font-weight-bold d-flex align-center">
                  <span>{{ item.product_name || `Termék #${index + 1}` }}</span>
                  <v-spacer />
                  <v-btn
                    icon
                    color="red"
                    @click="removeItem(index)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-card-title>

                <v-divider />

                <v-card-text>
                  <div class="section-title mb-2">Basic Information</div>

                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="item.issuer_name"
                        label="Issuer *"
                        outlined
                        dense
                        :error-messages="getItemValidationError(index, 'issuer_name')"
                        @input="onRequiredItemInput(index, 'issuer_name')"
                      />
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="item.manufacturer_name"
                        label="Manufacturer *"
                        outlined
                        dense
                        :error-messages="getItemValidationError(index, 'manufacturer_name')"
                        @input="onRequiredItemInput(index, 'manufacturer_name')"
                      />
                    </v-col>

                    <v-col cols="12">
                      <v-textarea
                        v-model="item.product_name"
                        label="Product name *"
                        outlined
                        dense
                        rows="2"
                        auto-grow
                        :error-messages="getItemValidationError(index, 'product_name')"
                        @input="onRequiredItemInput(index, 'product_name')"
                      />
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="item.num_of_pcs"
                        label="Number of pieces *"
                        outlined
                        dense
                        :error-messages="getItemValidationError(index, 'num_of_pcs')"
                        @input="onRequiredItemInput(index, 'num_of_pcs')"
                      />
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="item.quantity_unit"
                        label="Unit"
                        outlined
                        dense
                        :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'quantity_unit') }"
                        @input="clearItemOptionalWarning(index, 'quantity_unit')"
                      />
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="item.unit_weight"
                        label="Unit weight"
                        outlined
                        dense
                        :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'unit_weight') }"
                        @input="onOptionalItemInput(index, 'unit_weight')"
                      />
                    </v-col>

                    <v-col cols="12" md="3">
                      <v-text-field
                        :value="formatWeight(item.weight)"
                        label="Total weight"
                        outlined
                        dense
                        readonly
                      />
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="item.customs_tariff_number"
                        label="Tariff code"
                        outlined
                        dense
                        :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'customs_tariff_number') }"
                        @input="clearItemOptionalWarning(index, 'customs_tariff_number')"
                      />
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="item.production_date"
                        label="Date of manufacture/import"
                        type="date"
                        outlined
                        dense
                        :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'production_date') }"
                        @input="clearItemOptionalWarning(index, 'production_date')"
                      />
                    </v-col>

                    <v-col cols="12">
                      <v-text-field
                        v-model="item.usage_area"
                        label="Available for sale / usable"
                        outlined
                        dense
                        :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'usage_area') }"
                        @input="clearItemOptionalWarning(index, 'usage_area')"
                      />
                    </v-col>
                  </v-row>

                  <div class="section-title mt-4 mb-2">Technical and Operating Data</div>

                  <v-row dense>
                    <v-col cols="12">
                      <v-textarea
                        v-model="item.storage_instructions"
                        label="Shipping and Storage Requirements"
                        outlined
                        dense
                        rows="3"
                        auto-grow
                        :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'storage_instructions') }"
                        @input="clearItemOptionalWarning(index, 'storage_instructions')"
                      />
                    </v-col>

                    <v-col cols="12">
                      <v-text-field
                        v-model="item.packaging"
                        label="Packaging"
                        outlined
                        dense
                        :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'packaging') }"
                        @input="clearItemOptionalWarning(index, 'packaging')"
                      />
                    </v-col>

                    <v-col cols="12">
                      <v-textarea
                        v-model="item.technical_properties"
                        label="Key features of the product"
                        outlined
                        dense
                        rows="4"
                        auto-grow
                        :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'technical_properties') }"
                        @input="clearItemOptionalWarning(index, 'technical_properties')"
                      />
                    </v-col>

                    <v-col cols="12">
                      <v-text-field
                        v-model="item.inspection_method"
                        label="Test method"
                        outlined
                        dense
                        :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'inspection_method') }"
                        @input="clearItemOptionalWarning(index, 'inspection_method')"
                      />
                    </v-col>

                    <v-col cols="12">
                      <v-text-field
                        v-model="item.usage_instructions"
                        label="Instructions for Use and Handling"
                        outlined
                        dense
                        :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'usage_instructions') }"
                        @input="clearItemOptionalWarning(index, 'usage_instructions')"
                      />
                    </v-col>

                    <v-col cols="12">
                      <v-textarea
                        v-model="item.additional_data"
                        label="Other information / standards"
                        outlined
                        dense
                        rows="4"
                        auto-grow
                        :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'additional_data') }"
                        @input="clearItemOptionalWarning(index, 'additional_data')"
                      />
                    </v-col>

                    <v-col cols="12">
                      <v-textarea
                        v-model="item.serial_number"
                        label="Serial Number(s)"
                        outlined
                        dense
                        rows="4"
                        auto-grow
                        :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'serial_number') }"
                        @input="clearItemOptionalWarning(index, 'serial_number')"
                      />
                    </v-col>
                  </v-row>

                  <div class="section-title mt-4 mb-2">Close</div>

                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="item.issue_date"
                        label="Issue date"
                        type="date"
                        outlined
                        dense
                        :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'issue_date') }"
                        @input="clearItemOptionalWarning(index, 'issue_date')"
                      />
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="item.signature_label"
                        label="Signature caption"
                        outlined
                        dense
                        :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'signature_label') }"
                        @input="clearItemOptionalWarning(index, 'signature_label')"
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <v-alert
                v-if="form.items.length === 0"
                outlined
                dense
                type="warning"
              >
                No generatable products found in the order.
              </v-alert>
            </div>
          </div>

          <div v-if="showPreview" class="quality-preview-pane">
            <div class="quality-preview-toolbar">
              <div class="subtitle-2 font-weight-medium">Preview</div>

              <v-progress-circular
                v-if="previewLoading"
                indeterminate
                size="18"
                width="2"
                color="primary"
              />
            </div>

            <iframe
              v-if="previewHtml"
              :srcdoc="previewHtml"
              class="quality-preview-frame"
              frameborder="0"
            />

            <div v-else class="quality-preview-empty">
              No preview.
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn
          text
          color="primary"
          :loading="previewLoading"
          :disabled="form.items.length === 0"
          @click="openPreview"
        >
          {{ showPreview ? 'Hide preview' : 'Preview' }}
        </v-btn>

        <v-spacer />

        <v-btn text @click="closeDialog">Cancel</v-btn>

        <v-btn
          color="primary"
          :loading="loading"
          :disabled="form.items.length === 0"
          @click="generateDocument"
        >
          Generate All
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";

export default {
  name: "QualityCertificateDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    salesOrderId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      previewLoading: false,
      showPreview: false,
      previewHtml: "",
      previewDebounceTimer: null,
      previewRequestId: 0,
      validationErrors: {},
      optionalWarnings: {},
      form: {
        items: [],
      },
    };
  },

  computed: {
    dialogProxy: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.loadDefaults();
      } else {
        this.resetPreview();
      }
    },

    form: {
      deep: true,
      handler() {
        if (!this.showPreview) {
          return;
        }

        this.schedulePreviewRefresh();
      },
    },
  },

  methods: {
    async loadDefaults() {
      try {
        const { data } = await ApiService.get(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/quality-certificate-batch-defaults`
        );
          
        const items = Array.isArray(data.items) ? data.items : [];
          
        this.form = {
          items: items.map((item) => ({
            ...item,
            quantity: this.toNumericValue(item.quantity),
            unit_weight: this.toNumericValue(item.unit_weight),
            weight: this.toNumericValue(item.weight),
          })),
        };
      
        if (this.form.items.length === 0) {
          this.form.items.push(this.createEmptyItem());
        }
      
        this.recalculateAllWeights();
        this.validationErrors = {};
        this.optionalWarnings = {};
      } catch (e) {
        console.log(e);
      }
    },

    isEmpty(value) {
      return value === null || value === undefined || String(value).trim() === "";
    },

    hasItemOptionalWarning(index, field) {
      return Boolean(this.optionalWarnings[this.getItemValidationKey(index, field)]);
    },

    clearItemOptionalWarning(index, field) {
      const key = this.getItemValidationKey(index, field);

      if (this.optionalWarnings[key]) {
        this.$delete(this.optionalWarnings, key);
      }
    },

    getItemValidationKey(index, field) {
      return `items.${index}.${field}`;
    },

    getItemValidationError(index, field) {
      return this.validationErrors[this.getItemValidationKey(index, field)] || [];
    },

    clearItemValidationError(index, field) {
      const key = this.getItemValidationKey(index, field);

      if (this.validationErrors[key]) {
        this.$delete(this.validationErrors, key);
      }
    },

    onRequiredItemInput(index, field) {
      this.clearItemValidationError(index, field);

      if (field === "num_of_pcs") {
        this.recalculateItemWeight(index);
      }
    },

    onOptionalItemInput(index, field) {
      this.clearItemOptionalWarning(index, field);

      if (field === "unit_weight") {
        this.recalculateItemWeight(index);
      }
    },

    validateForm() {
      const errors = {};

      if (!Array.isArray(this.form.items) || this.form.items.length === 0) {
        errors.items = ["At least one item is required."];
      } else {
        this.form.items.forEach((item, index) => {
          [
            { key: "issuer_name", label: "Issuer" },
            { key: "manufacturer_name", label: "Manufacturer" },
            { key: "product_name", label: "Product name" },
            { key: "num_of_pcs", label: "Number of pieces" },
          ].forEach((field) => {
            if (this.isEmpty(item[field.key])) {
              errors[this.getItemValidationKey(index, field.key)] = [
                `${field.label} is required.`,
              ];
            }
          });
        });
      }

      this.validationErrors = errors;

      return Object.keys(errors).length === 0;
    },

    async showValidationAlert() {
      await Swal.fire({
        title: "Missing required data",
        icon: "warning",
        text: "Please fill in all required fields before creating the document.",
        confirmButtonText: "OK",
      });
    },

    getMissingOptionalFields() {
      const missingFields = [];
      const optionalItemFields = [
        { key: "quantity_unit", label: "Unit" },
        { key: "unit_weight", label: "Unit weight" },
        { key: "customs_tariff_number", label: "Tariff code" },
        { key: "production_date", label: "Date of manufacture/import" },
        { key: "usage_area", label: "Available for sale / usable" },
        { key: "storage_instructions", label: "Shipping and Storage Requirements" },
        { key: "packaging", label: "Packaging" },
        { key: "technical_properties", label: "Key features of the product" },
        { key: "inspection_method", label: "Test method" },
        { key: "usage_instructions", label: "Instructions for Use and Handling" },
        { key: "additional_data", label: "Other information / standards" },
        { key: "serial_number", label: "Serial Number(s)" },
        { key: "issue_date", label: "Issue date" },
        { key: "signature_label", label: "Signature caption" },
      ];

      if (Array.isArray(this.form.items)) {
        this.form.items.forEach((item, index) => {
          optionalItemFields.forEach((field) => {
            if (this.isEmpty(item[field.key])) {
              missingFields.push({
                key: this.getItemValidationKey(index, field.key),
                label: `${item.product_name} - ${field.label}`,
              });
            }
          });
        });
      }

      return missingFields;
    },

    markOptionalWarnings(fields) {
      const warnings = {};

      fields.forEach((field) => {
        warnings[field.key] = true;
      });

      this.optionalWarnings = warnings;
    },

    async confirmMissingOptionalFields() {
      const missingFields = this.getMissingOptionalFields();

      if (!missingFields.length) {
        this.optionalWarnings = {};
        return true;
      }

      const fieldList = missingFields
        .map((field) => `<li>${field.label}</li>`)
        .join("");

      const result = await Swal.fire({
        title: "Some fields are empty",
        icon: "warning",
        html: `The following fields are not filled:<br><ul style="text-align:left;margin-top:12px;">${fieldList}</ul><br>Do you want to continue anyway?`,
        showCancelButton: true,
        confirmButtonText: "Yes, generate document",
        cancelButtonText: "No, I will fill them",
        confirmButtonColor: "#00c300",
        width: "650px",
      });

      if (result.isConfirmed) {
        this.optionalWarnings = {};
        return true;
      }

      this.markOptionalWarnings(missingFields);
      return false;
    },

    toNumericValue(value) {
      if (value === null || value === undefined || value === "") {
        return 0;
      }

      const normalized = String(value).replace(",", ".");
      const parsed = parseFloat(normalized);

      return Number.isNaN(parsed) ? 0 : parsed;
    },

    formatWeight(value) {
      const numeric = this.toNumericValue(value);

      if (!numeric) {
        return "0";
      }

      return String(parseFloat(numeric.toFixed(2)));
    },

    recalculateItemWeight(index) {
      const item = this.form.items[index];
      if (!item) {
        return;
      }

      const quantity = this.toNumericValue(item.quantity);
      const unitWeight = this.toNumericValue(item.unit_weight);
      const num_of_pcs = this.toNumericValue(item.num_of_pcs);

      this.$set(item, "quantity", quantity);
      this.$set(item, "weight", quantity * unitWeight * num_of_pcs);
    },

    recalculateAllWeights() {
      this.form.items.forEach((item, index) => {
        this.recalculateItemWeight(index);
      });
    },

    resetPreview() {
      this.showPreview = false;
      this.previewHtml = "";
      this.previewLoading = false;
      this.validationErrors = {};
      this.optionalWarnings = {};

      if (this.previewDebounceTimer) {
        clearTimeout(this.previewDebounceTimer);
        this.previewDebounceTimer = null;
      }
    },

    closeDialog() {
      this.resetPreview();
      this.dialogProxy = false;
    },

    schedulePreviewRefresh() {
      if (this.previewDebounceTimer) {
        clearTimeout(this.previewDebounceTimer);
      }

      this.previewDebounceTimer = setTimeout(() => {
        this.refreshPreview();
      }, 400);
    },

    async openPreview() {
      if(this.showPreview === true){
        this.showPreview = false;
      }else{
        this.showPreview = true;
      }

      await this.refreshPreview();
    },

    buildPayload() {
      return {
        items: this.form.items.map((item) => ({
          ...item,
          quantity: this.toNumericValue(item.quantity),
          unit_weight: this.toNumericValue(item.unit_weight),
          weight: this.toNumericValue(item.unit_weight) * this.toNumericValue(item.quantity) * this.toNumericValue(item.num_of_pcs),
        })),
      };
    },

    async refreshPreview() {
      const requestId = ++this.previewRequestId;
      this.previewLoading = true;

      try {
        const { data } = await ApiService.post(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/previewQualityCertificateBatch`,
          this.buildPayload()
        );

        if (requestId !== this.previewRequestId) {
          return;
        }

        this.previewHtml = data.html || "";
      } catch (error) {
        console.log(error);
      } finally {
        if (requestId === this.previewRequestId) {
          this.previewLoading = false;
        }
      }
    },

    async generateDocument() {
      if (!this.validateForm()) {
        await this.showValidationAlert();
        return;
      }

      const missingFields = this.getMissingOptionalFields();

      this.markOptionalWarnings(missingFields);

      const canContinue = await this.confirmMissingOptionalFields();

      if (!canContinue) {
        return;
      }

      this.loading = true;

      try {
        const { data } = await ApiService.post(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/generateQualityCertificateBatch`,
          this.buildPayload()
        );

        if (data && data.url) {
          window.open(data.url, "_blank");
        }

        this.$emit("generated", data);
        this.closeDialog();
      } catch (error) {
        Swal.fire({
          title: "Error",
          icon: "error",
          text: error?.response?.data?.message ?? "The document could not be created.",
        });

        console.log(error);
      } finally {
        this.loading = false;
      }
    },

    createEmptyItem() {
      const today = new Date().toISOString().slice(0, 10);

      return {
        sales_order_item_id: null,
        item_id: null,
      
        issuer_name: "Kismetál Kft.",
        manufacturer_name: "Kismetál Kft.",
      
        product_name: "",
        quantity: 1,
        quantity_unit: "db",
      
        unit_weight: 0,
        weight: 0,
      
        customs_tariff_number: "84368090",
        production_date: today,
        usage_area: "mezőgazdaság",
      
        storage_instructions:
          "Gyártóműi ellenőrzés után készre szerelve, szállítva.\nFedett, száraz helyen tárolandó.",
        packaging: "Nincs",
        technical_properties:
          "A termék a próbaüzem során funkcionális és munkavédelmi követelményeknek megfelelően működött.",
        inspection_method: "Próbaüzem.",
        usage_instructions: "Szerelési utasítás szerint.",
        additional_data:
          "A forgalmazó kijelenti, hogy a termék megfelel az alábbi EN 60204-1; TS EN 12100:2010; TS EN ISO 13857 követelményeknek.\nNyilatkozunk, a fenti terméket az Európai Unióban gyártották.",
      
        serial_number: null,
        issue_date: today,
        signature_label: "aláírás, bélyegző",
      };
    },

    addItem() {
      this.form.items.push(this.createEmptyItem());
    },

    removeItem(index) {
      this.form.items.splice(index, 1);
      this.validationErrors = {};
      this.optionalWarnings = {};
    },
  },
};
</script>

<style scoped>
.quality-dialog-layout {
  display: flex;
  min-height: 70vh;
}

.quality-form-pane {
  flex: 0 0 100%;
  max-width: 100%;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
}
.quality-form-pane.with-preview {
  flex: 0 0 40%;
  max-width: 40%;
}

.quality-preview-pane {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  padding: 16px;
  min-width: 0;
  height: 100%;
}

.quality-preview-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.quality-preview-frame {
  width: 100%;
  height: calc(100vh);
  background: #fff;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
}

.quality-preview-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(70vh - 32px);
  background: #fff;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  color: #757575;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
}

.optional-warning-field >>> .v-input__slot {
  background-color: #fff8e1 !important;
}

.optional-warning-field >>> fieldset {
  border-color: #f9a825 !important;
}
</style>